import { api } from "../utils/axios";
import type {
  CreateHelpCenterSessionPayload,
  HelpCenterAvailabilityQuery,
  HelpCenterAvailabilityResponse,
  HelpCenterChatMessage,
} from "../../Reservation/helpcenter.types";

export class HelpCenterApiError extends Error {
  status?: number;
  details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = "HelpCenterApiError";
    this.status = status;
    this.details = details;
  }
}

const toApiError = (error: any, fallbackMessage: string): HelpCenterApiError => {
  const status = error?.response?.status;
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.formattedMessage ||
    fallbackMessage;

  return new HelpCenterApiError(message, status, error?.response?.data);
};

export const getHelpCenterAvailability = async (
  query: HelpCenterAvailabilityQuery
): Promise<HelpCenterAvailabilityResponse> => {
  try {
    const response = await api.get("/api/help-center/availability", {
      params: query,
      withCredentials: true,
    });

    return {
      slots: response.data?.slots ?? [],
      bookedDays: response.data?.bookedDays ?? [],
    };
  } catch (error) {
    throw toApiError(error, "Failed to fetch available support slots.");
  }
};

export const createHelpCenterSession = async (
  payload: CreateHelpCenterSessionPayload
): Promise<{ success: boolean; id?: string; meetingLink?: string }> => {
  try {
    const response = await api.post("/api/help-center/sessions", payload, {
      withCredentials: true,
    });

    return {
      success: !!response.data?.success,
      id: response.data?.id,
      meetingLink: response.data?.meetingLink,
    };
  } catch (error) {
    throw toApiError(error, "Failed to book support session.");
  }
};

export const sendHelpCenterChatMessage = async (
  payload: HelpCenterChatMessage
): Promise<{ reply: string }> => {
  try {
    const response = await api.post("/api/help-center/chat", payload, {
      withCredentials: true,
    });

    return {
      reply:
        response.data?.reply ||
        response.data?.message ||
        "Thanks for your message. Our team will get back to you shortly.",
    };
  } catch (error) {
    throw toApiError(error, "Failed to send support message.");
  }
};
